import { useEffect, useMemo, useState, type JSX } from 'react';
import { buildAvailableUnitGroups } from '@/components/public/units';
import { usePublicPropertyList } from '@/hooks/usePublicProperties';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import { mergeContentWithDefaults } from '@/lib/utils/contentDefaults';
import { PublicAvailabilitiesView } from './components/PublicAvailabilitiesView';
import { AvailabilitiesFilterControls } from './components/AvailabilitiesFilterControls';
import {
  buildAvailabilityFilterSummaryChips,
  countAvailabilityActiveFilters,
  createEmptyAvailabilityFilters,
  createAvailabilityFilterChipActions,
  filtersEqualAvailabilityFilters,
  parseAvailabilityFilters,
  type AvailabilityFiltersState,
} from './availabilitiesFilters';
import {
  buildAssetTypeOptions,
  buildAvailabilityRangeBounds,
  filterAvailableGroups,
  sortAvailableGroups,
} from './availabilitiesUtils';
import {
  buildAvailabilityLabels,
  buildResultsSummary,
  clampAvailabilityFilters,
} from './availabilitiesViewHelpers';
import { DEFAULT_AVAILABILITIES_CONTENT } from './availabilitiesContent';
import { useAvailabilitiesViewMode } from './useAvailabilitiesViewMode';
export function PublicAvailabilities(): JSX.Element {
  const { data, isLoading, isFetching, error } = usePublicPropertyList({ pageSize: 60 });
  const siteSettingsQuery = useSiteSettingsDetail();
  const publicContent = siteSettingsQuery.data?.publicContent ?? null;
  const availabilitiesContent = mergeContentWithDefaults(
    publicContent?.availabilitiesPage,
    DEFAULT_AVAILABILITIES_CONTENT,
  );
  const normalizedAvailabilitiesContent = {
    ...availabilitiesContent,
    filterControls: {
      ...availabilitiesContent.filterControls,
      priceTitle: 'Monthly rent ($)',
      priceDescription: 'Total rent amount monthly',
    },
  };
  const alertsContent = publicContent?.shared.availabilityAlerts;
  const availabilityLabels = buildAvailabilityLabels(publicContent);
  const [filters, setFilters] = useState<AvailabilityFiltersState>(() => {
    if (typeof window === 'undefined') {
      return createEmptyAvailabilityFilters();
    }
    const stored = parseAvailabilityFilters(
      window.localStorage.getItem('public_availabilities_filters'),
    );
    const resolved = stored ?? createEmptyAvailabilityFilters();
    return {
      ...resolved,
      monthlyRent: {
        ...resolved.monthlyRent,
        min: null,
      },
    };
  });
  const [viewMode, setViewMode] = useAvailabilitiesViewMode();
  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [drawerDraft, setDrawerDraft] = useState<AvailabilityFiltersState>(
    createEmptyAvailabilityFilters,
  );
  useEffect(() => {
    if (!isFilterDrawerOpen) {
      setDrawerDraft(filters);
    }
  }, [filters, isFilterDrawerOpen]);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem('public_availabilities_filters', JSON.stringify(filters));
  }, [filters]);
  const assetTypeOptions = useMemo(() => buildAssetTypeOptions(data?.items ?? []), [data]);
  const availableGroups = buildAvailableUnitGroups(data?.items ?? []);
  const groups = sortAvailableGroups(filterAvailableGroups(availableGroups, filters));
  const rangeBounds = buildAvailabilityRangeBounds(availableGroups);
  const totalAvailableUnits = useMemo(
    () => availableGroups.reduce((sum, group) => sum + group.units.length, 0),
    [availableGroups],
  );
  useEffect(() => {
    if (!rangeBounds.monthlyRent && !rangeBounds.areaSf) {
      return;
    }
    setFilters((current) => {
      const next = clampAvailabilityFilters(current, rangeBounds);
      return filtersEqualAvailabilityFilters(current, next) ? current : next;
    });
    setDrawerDraft((current) => {
      const next = clampAvailabilityFilters(current, rangeBounds);
      return filtersEqualAvailabilityFilters(current, next) ? current : next;
    });
  }, [rangeBounds]);
  const filteredUnitCount = useMemo(
    () => groups.reduce((sum, group) => sum + group.units.length, 0),
    [groups],
  );
  const activeFilterCount = useMemo(() => countAvailabilityActiveFilters(filters), [filters]);
  const resultsSummary = useMemo(
    () =>
      buildResultsSummary({
        filteredUnitCount,
        groupsCount: groups.length,
        totalAvailableUnits,
        hasActiveFilters: activeFilterCount > 0,
        availableGroupsCount: availableGroups.length,
      }),
    [
      availableGroups.length,
      activeFilterCount,
      filteredUnitCount,
      groups.length,
      totalAvailableUnits,
    ],
  );
  const resetFilters = () => {
    const empty = createEmptyAvailabilityFilters();
    setFilters(empty);
    setDrawerDraft(empty);
  };
  const summaryChipLabels = {
    priceLabel: normalizedAvailabilitiesContent.filters.chipPriceLabel,
    sizeLabel: normalizedAvailabilitiesContent.filters.chipSizeLabel,
    useLabel: normalizedAvailabilitiesContent.filters.chipUseLabel,
  };
  const summaryChips = buildAvailabilityFilterSummaryChips(
    filters,
    createAvailabilityFilterChipActions(setFilters),
    summaryChipLabels,
  );
  const drawerSummaryChips = buildAvailabilityFilterSummaryChips(
    drawerDraft,
    createAvailabilityFilterChipActions(setDrawerDraft),
    summaryChipLabels,
  );
  const handleDrawerOpenChange = (open: boolean) => {
    setFilterDrawerOpen(open);
    if (open) {
      setDrawerDraft(filters);
    }
  };
  const drawerControls = useMemo(
    () => [
      {
        id: 'availabilities-filters',
        control: (
          <AvailabilitiesFilterControls
            value={drawerDraft}
            onChange={setDrawerDraft}
            assetTypeOptions={assetTypeOptions}
            content={normalizedAvailabilitiesContent.filterControls}
            monthlyRentBounds={rangeBounds.monthlyRent}
          />
        ),
      },
    ],
    [assetTypeOptions, normalizedAvailabilitiesContent.filterControls, drawerDraft, rangeBounds],
  );
  return (
    <PublicAvailabilitiesView
      availabilitiesContent={normalizedAvailabilitiesContent}
      alertsContent={alertsContent}
      availabilityLabels={availabilityLabels}
      data={data ?? null}
      isLoading={isLoading || isFetching}
      error={error}
      assetTypeOptions={assetTypeOptions}
      filters={filters}
      onFiltersChange={setFilters}
      activeFilterCount={activeFilterCount}
      hasActiveFilters={activeFilterCount > 0}
      onResetFilters={resetFilters}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      resultsSummary={resultsSummary}
      groups={groups}
      availableGroupsCount={availableGroups.length}
      summaryChips={summaryChips}
      drawerSummaryChips={drawerSummaryChips}
      drawerControls={drawerControls}
      isFilterDrawerOpen={isFilterDrawerOpen}
      onFilterDrawerChange={handleDrawerOpenChange}
      onDrawerApply={() => {
        setFilters(drawerDraft);
      }}
      onDrawerReset={() => {
        setDrawerDraft(createEmptyAvailabilityFilters());
      }}
      isApplyDisabled={filtersEqualAvailabilityFilters(drawerDraft, filters)}
      monthlyRentBounds={rangeBounds.monthlyRent}
    />
  );
}
